import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import PdfFooter from "../../components/Report/PdfFooter";
import styles from "../../components/Report/Report.module.css";
import opioidStyles from "../../components/Report/OpioidAdherence.module.css";
import pointCardStyles from "../../components/Report/PointCard.module.css";
import { IBillingCycleData } from "../../hooks/patients/useFetchBillingCyclePdf";
import { IImpactOfLivingData } from "../../hooks/patients/useImpactOfLivingReport";
import { IAffectData } from "../../hooks/patients/useFetchAffect";
import { useGetBillingDetailsPage } from "../../hooks/PhysicianReportPage.ts";
import NewReportHeader from "./NewReportHeader";
import NewDateSection from "./NewDateSection";
import NewPlatformInfo, { NewRTMUsed } from "./NewPlatformInfo";
import NewScoreSection from "./NewScoreSection";
import NewCardBox from "./NewCardBox";
import NewScoreCard from "./NewScoreCard";
import NewOpioidAdherenceCard from "./NewOpiodAdherenceCard";
import NewPointCard from "./NewPointCard";

export interface ReportPageHandles {
  getReportElement: () => HTMLDivElement | null;
}

interface Props {
  children?: React.ReactNode;
  title?: string;
  patientId?: string;
  billingCycleId?: string;
  isReadyForDownload?: boolean;
  billingCycleData: IBillingCycleData | null;
  sideEffectQuestions?: {
    _id: string;
    billedCycle: string;
    sideEffect: string[];
    text: string;
  }[];
  customWidth?: string;
  customHeight?: string;
  billingCycleDataLoading: boolean;
  sideEffectQuestionsLoading?: boolean;
  customStyle?: React.CSSProperties;
  refetchImpact?: () => Promise<IImpactOfLivingData | null>;
  isImpactDataLoading?: boolean;
  impactOfLivingData?: IImpactOfLivingData | null;
  isAffectDataLoading?: boolean;
  affectData?: IAffectData | null;
  isSideEffectsLoading?: boolean;
  sideEffects?: string[] | null;
  isOpioidDataLoading?: boolean;
  opioidData: {
    isPatientCurrentlyPrescribed: string;
    hasPatientTakeMedication: string;
    hasPatientTakeOtherMedication: string[];
    askFirstQuestion?: string;
    askMedicationQuestion?: string;
  } | null;
  riskRatingLoading?: boolean;
  riskRatingData: {
    verificationResult: string;
  } | null;
}

const NewReportPage = forwardRef<ReportPageHandles, Props>(
  (
    {
      billingCycleData,
      billingCycleDataLoading,
      customWidth,
      patientId = "",
      customHeight,
      customStyle,
      isReadyForDownload = false,
      billingCycleId,
      impactOfLivingData,
      isImpactDataLoading,
      isAffectDataLoading,
      affectData,
      sideEffects,
      isSideEffectsLoading,
      isOpioidDataLoading,
      opioidData,
      riskRatingLoading,
      riskRatingData,
    },
    ref
  ) => {
    const reportRef = useRef<HTMLDivElement>(null);

    // hooks
    const { getBillingDetails, reportBillingDetails } =
      useGetBillingDetailsPage({
        billingCycleId: billingCycleId,
      });

    useEffect(() => {
      getBillingDetails();
    }, [billingCycleId]);

    const transformedOpioidData = opioidData
      ? {
          isPatientCurrentlyPrescribed:
            opioidData.isPatientCurrentlyPrescribed as "yes" | "no",
          hasPatientTakeMedication: opioidData.hasPatientTakeMedication,
          hasPatientTakeOtherMedication:
            opioidData.hasPatientTakeOtherMedication,
          askFirstQuestion: opioidData.askFirstQuestion,
          askMedicationQuestion: opioidData.askMedicationQuestion,
        }
      : undefined;

    useImperativeHandle(ref, () => ({
      getReportElement: () => reportRef.current,
    }));

    const isPdfGeneration = typeof isReadyForDownload === "boolean";
    const isVisible = !isPdfGeneration || isReadyForDownload;

    // Validate if affect data has meaningful content
    const hasAffectData =
      affectData && (affectData.depressionScore || affectData.anxietyScore);

    // Validate if opioid data has meaningful content (not an error response)
    const hasValidOpioidData =
      opioidData &&
      opioidData.hasPatientTakeOtherMedication &&
      Array.isArray(opioidData.hasPatientTakeOtherMedication);

    const affectsData = [
      {
        label: "Depression",
        positive: {
          value: affectData?.depressionScore === "high" ? 1 : 0,
          risk: affectData?.depressionScore === "high" ? "High" : "Low",
          trend: "stable" as const,
          lastUpdated: new Date().toISOString(),
        },
        negative: {
          value: affectData?.depressionScore === "high" ? 0 : 1,
          risk: affectData?.depressionScore === "high" ? "Low" : "High",
          trend: "stable" as const,
          lastUpdated: new Date().toISOString(),
        },
      },
      {
        label: "Anxiety",
        positive: {
          value: affectData?.anxietyScore === "high" ? 1 : 0,
          risk: affectData?.anxietyScore === "high" ? "High" : "Low",
          trend: "stable" as const,
          lastUpdated: new Date().toISOString(),
        },
        negative: {
          value: affectData?.anxietyScore === "high" ? 0 : 1,
          risk: affectData?.anxietyScore === "high" ? "Low" : "High",
          trend: "stable" as const,
          lastUpdated: new Date().toISOString(),
        },
      },
    ];

    return (
      <div
        ref={reportRef}
        className={`${styles.page}`}
        style={{
          width: customWidth,
          height: customHeight,
          // pageBreakAfter: "always",
          visibility: isVisible ? "visible" : "hidden",
          position:
            isPdfGeneration && !isReadyForDownload ? "absolute" : "static",
          left: isPdfGeneration && !isReadyForDownload ? "-9999px" : "0",
          ...(customStyle || {}),
        }}
      >
        <div className={styles.content}>
          <div className={styles.fixedSection}>
            <NewReportHeader
              billingCycleData={billingCycleData}
              billingCycleDataLoading={billingCycleDataLoading}
            />
          </div>

          <div className={styles.scrollableContent}>
            <div className={styles.innerContent}>
              <NewDateSection
                billingCycleData={billingCycleData}
                consentDate={reportBillingDetails?.data?.data?.consentDate}
              />
              <NewRTMUsed data={reportBillingDetails} />

              <div className={styles.summarySection}>
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <NewPlatformInfo data={reportBillingDetails} />
                    <NewScoreSection
                      patientId={patientId}
                      billingCycleId={billingCycleId || ""}
                      riskRatingData={riskRatingData}
                      riskRatingLoading={riskRatingLoading}
                    />
                  </div>
                  <NewCardBox
                    heading="FUNCTION LEVEL"
                    impactData={impactOfLivingData}
                    showFunctionLevelTable={true}
                    intakeFormDataloading={isImpactDataLoading}
                  />
                </div>

                <div className={styles.scoreCardsRow}>
                  {isAffectDataLoading ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>AFFECT</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        Loading...
                      </div>
                    </div>
                  ) : !hasAffectData ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>AFFECT</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        No data available
                      </div>
                    </div>
                  ) : (
                    <NewScoreCard
                      title="AFFECT"
                      affectType="dual"
                      lastUpdated={new Date().toLocaleDateString()}
                      affects={affectsData}
                      onViewDetails={() => {}}
                    />
                  )}
                  {isOpioidDataLoading ? (
                    <div className={opioidStyles.loadingCard}>
                      <div className={opioidStyles.loadingHeader}>
                        OPIOID ADHERENCE
                      </div>
                      <div className={opioidStyles.loadingContent}>
                        Loading...
                      </div>
                    </div>
                  ) : (
                    <NewOpioidAdherenceCard data={transformedOpioidData} />
                  )}
                </div>

                <div className={styles.newCardsRow}>
                  {isOpioidDataLoading ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>ALTERNATIVE MEDICATIONS</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        Loading...
                      </div>
                    </div>
                  ) : !hasValidOpioidData ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>ALTERNATIVE MEDICATIONS</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        No data available
                      </div>
                    </div>
                  ) : (
                    <NewPointCard
                      title="ALTERNATIVE MEDICATIONS"
                      items={
                        opioidData.hasPatientTakeOtherMedication.length > 0
                          ? opioidData.hasPatientTakeOtherMedication.map(
                              (text, index) => ({
                                text:
                                  text && text.toLowerCase() === "n/a"
                                    ? "N/A"
                                    : text || "N/A",
                              })
                            )
                          : [{ text: "N/A" }]
                      }
                    />
                  )}

                  {isSideEffectsLoading ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>SIDE EFFECTS</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        Loading...
                      </div>
                    </div>
                  ) : (
                    <NewPointCard
                      title="SIDE EFFECTS"
                      useWarningIcon={true}
                      items={
                        sideEffects && sideEffects.length > 0
                          ? sideEffects.map((effect, index) => ({
                              text: effect,
                            }))
                          : []
                      }
                    />
                  )}
                </div>
              </div>
            </div>
            <PdfFooter />
          </div>
        </div>
      </div>
    );
  }
);

export default NewReportPage;
